import { cn } from '@/lib/utils';

type Variant = 'primary' | 'stacked' | 'symbol' | 'wordmark';
type Tone = 'humus' | 'white' | 'alternate';

const SRC_MAP: Record<Variant, Record<Tone, string>> = {
  primary: {
    humus: '/brand/virro-primary-humus.svg',
    white: '/brand/virro-primary-white.svg',
    alternate: '/brand/virro-primary-alternate.svg',
  },
  stacked: {
    humus: '/brand/virro-stacked-humus.svg',
    white: '/brand/virro-stacked-white.svg',
    alternate: '/brand/virro-stacked-alternate.svg',
  },
  symbol: {
    humus: '/brand/virro-symbol-humus.svg',
    white: '/brand/virro-symbol-white.svg',
    alternate: '/brand/virro-symbol-alternate.svg',
  },
  wordmark: {
    humus: '/brand/virro-wordmark-humus.svg',
    white: '/brand/virro-wordmark-white.svg',
    alternate: '/brand/virro-wordmark-humus.svg', // wordmark has no alternate; falls back
  },
};

interface LogoProps {
  /**
   * Brand approves these three lockup variants (p.11-13):
   *   - primary  — symbol + wordmark, horizontal (default)
   *   - stacked  — symbol above wordmark
   *   - symbol   — knot mark only (use only when brand is already established)
   *   - wordmark — wordmark only (graphic-device usage)
   */
  variant?: Variant;
  /**
   * Approved colorways (p.10):
   *   - humus     — Humus on light backgrounds (default)
   *   - white     — White on dark / Humus surfaces
   *   - alternate — Bright Moss + white wordmark, ON Humus surfaces ONLY
   */
  tone?: Tone;
  className?: string;
}

/**
 * Production logo — references the SVG files exported into public/brand/
 * from brand-assets-archive/logos/Regular/. Per Brand Guidelines p.22,
 * never recolor, rotate, restroke, or reletter — just use as-is.
 *
 * Maintain clear space (p.23): equal to the height of the "o" in the wordmark.
 * The hosting layout should pad accordingly; this component renders raw.
 */
export function Logo({ variant = 'primary', tone = 'humus', className }: LogoProps) {
  const src = SRC_MAP[variant][tone];
  return (
    <img
      src={src}
      alt="Virro"
      className={cn('select-none', className)}
      draggable={false}
    />
  );
}
